'use client'

import { useEffect, useRef, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * A decorative section backdrop. While its section is on screen it writes
 * --sp (scroll progress, -1 entering from below to 1 leaving at the top) and
 * --lx / --ly (pointer position over the section, 0..1) for the layers inside
 * to read, and marks itself data-live="off" when offscreen so its CSS
 * animations pause. Does nothing for visitors who ask for reduced motion.
 */
export function ParallaxBackdrop({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    const host = el?.parentElement
    if (!el || !host) return

    let visible = false
    let frame = 0
    let pointer: { x: number; y: number } | null = null
    const still = window.matchMedia('(prefers-reduced-motion: reduce)')

    const update = () => {
      frame = 0
      if (still.matches) return
      const rect = host.getBoundingClientRect()
      const vh = window.innerHeight || 1
      const progress = -(rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2)
      el.style.setProperty('--sp', Math.max(-1, Math.min(1, progress)).toFixed(3))
      if (pointer) {
        el.style.setProperty('--lx', ((pointer.x - rect.left) / rect.width).toFixed(3))
        el.style.setProperty('--ly', ((pointer.y - rect.top) / rect.height).toFixed(3))
        pointer = null
      }
    }
    const schedule = () => {
      if (visible && !frame) frame = requestAnimationFrame(update)
    }
    const onPointer = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return
      pointer = { x: event.clientX, y: event.clientY }
      schedule()
    }

    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
      el.dataset.live = visible ? 'on' : 'off'
      if (visible) schedule()
    })
    io.observe(host)

    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    host.addEventListener('pointermove', onPointer)
    return () => {
      io.disconnect()
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      host.removeEventListener('pointermove', onPointer)
    }
  }, [])

  return (
    <div ref={ref} aria-hidden className={cn('pointer-events-none absolute inset-0 -z-10', className)}>
      {children}
    </div>
  )
}
